import type { AuditRecordDetailDTO } from "@/modules/internal-audit/domain/contracts";
import { formatDateLocal, formatDateTimeLocal } from "@/shared/lib/date-time";
import { resolveImageSrc } from "@/shared/ui/AppImage";

interface AuditRecordPdfPreviewContentProps {
  record: AuditRecordDetailDTO;
}

export function AuditRecordPdfPreviewContent({
  record,
}: AuditRecordPdfPreviewContentProps) {
  const getResultText = (result: string) => {
    switch (result) {
      case "pass":
        return "ຜ່ານ";
      case "fail":
        return "ບໍ່ຜ່ານ";
      case "needs_improvement":
        return "ຕ້ອງປັບປຸງ";
      default:
        return result || "-";
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case "completed":
        return "ແລ້ວ";
      case "draft":
        return "ຮ່າງ";
      default:
        return status || "-";
    }
  };

  return (
    <div
      id="audit-record-pdf-content"
      className="mx-auto max-w-4xl bg-white p-8 font-serif text-gray-900 leading-relaxed print:max-w-none print:p-0 print:font-serif print:text-gray-900 print:leading-relaxed"
      style={{
        fontFamily: '"Times New Roman", Times, serif',
        lineHeight: "1.5",
        color: "#1a1a1a",
      }}
    >
      {/* PDF Header - Letterhead Style */}
      <div className="mb-8 border-gray-800 border-b-4 pb-6 print:mb-6">
        <div className="mb-6 text-center">
          <h1 className="font-bold text-4xl text-gray-800 uppercase tracking-wider">
            FREEDOM Sole Co.Ltd
          </h1>
          <p className="mt-2 text-gray-600 text-lg">ບັນທຶກການກວດສອບພາຍໃນ</p>
          <div className="mx-auto mt-4 h-1 w-24 bg-gray-800" />
        </div>

        <div className="flex items-start justify-between">
          <div className="text-sm">
            <p className="font-semibold">
              ເອກະສານເລກທີ:{" "}
              <span className="font-normal">{`Record-${record?.id.slice(-6).toUpperCase()}`}</span>
            </p>
            <p className="mt-1 font-semibold">
              ຜະແນການກວດສອບ:{" "}
              <span className="font-normal">
                {record?.auditPlanName || "ບໍ່ໄດ້ກໍານົດ"}
              </span>
            </p>
          </div>
          <div className="text-right text-sm">
            <p className="font-semibold">ວັນທີກວດສອບ:</p>
            <p className="font-normal">
              {formatDateLocal(record?.auditDate || "")}
            </p>
            <p className="mt-2 font-semibold">ວັນທີພິມ:</p>
            <p className="font-normal">{formatDateLocal(new Date())}</p>
          </div>
        </div>
      </div>

      {/* Record Title and Status */}
      <div className="mb-12 text-center">
        <h2 className="mb-6 border-gray-300 border-b-2 pb-4 font-bold text-3xl text-gray-800 uppercase tracking-wide">
          ບັນທຶກການກວດສອບພາຍໃນ
        </h2>
        <p className="font-semibold text-gray-700 text-xl">
          ສະຖານະ: {getStatusText(record?.status)} | ຜົນການກວດສອບ:{" "}
          {getResultText(record?.overallResult)}
        </p>
      </div>

      {/* Section 1: ข้อมูลพื้นฐาน */}
      <div className="mb-10">
        <h3 className="mb-6 border-gray-300 border-b-2 pb-2 font-bold text-gray-800 text-xl uppercase tracking-wide">
          1. ຂໍ້ມູນພື້ນຖານ
        </h3>

        <table className="mb-6 w-full border-collapse border-2 border-gray-400">
          <tbody>
            <tr className="border-gray-300 border-b">
              <td className="w-1/3 border-gray-300 border-r bg-gray-100 p-4 font-semibold text-gray-700">
                ຜະແນການກວດສອບ
              </td>
              <td className="p-4 text-sm leading-relaxed">
                {record?.auditPlanName || "ບໍ່ໄດ້ກໍານົດ"}
              </td>
            </tr>
            <tr className="border-gray-300 border-b">
              <td className="border-gray-300 border-r bg-gray-100 p-4 font-semibold text-gray-700">
                ປະເພດການກວດສອບ
              </td>
              <td className="p-4 text-sm">
                {record?.auditTypeName || "ບໍ່ໄດ້ກໍານົດ"}
              </td>
            </tr>
            <tr className="border-gray-300 border-b">
              <td className="border-gray-300 border-r bg-gray-100 p-4 font-semibold text-gray-700">
                ພະແນກ
              </td>
              <td className="p-4 text-sm">
                {record?.departmentName || "ບໍ່ໄດ້ກໍານົດ"}
              </td>
            </tr>
            <tr className="border-gray-300 border-b">
              <td className="border-gray-300 border-r bg-gray-100 p-4 font-semibold text-gray-700">
                ວັນທີກວດສອບ
              </td>
              <td className="p-4 text-sm">
                {formatDateTimeLocal(record?.auditDate || "")}
              </td>
            </tr>
            <tr className="border-gray-300 border-b">
              <td className="border-gray-300 border-r bg-gray-100 p-4 font-semibold text-gray-700">
                ຜູ້ກວດສອບ
              </td>
              <td className="p-4 text-sm">
                {record?.auditorName || "ບໍ່ໄດ້ກໍານົດ"}
              </td>
            </tr>
            <tr className="border-gray-300 border-b">
              <td className="border-gray-300 border-r bg-gray-100 p-4 font-semibold text-gray-700">
                ສິ່ງທີ່ກວດສອບ
              </td>
              <td className="p-4 text-sm leading-relaxed">
                {record?.auditScope || "-"}
              </td>
            </tr>
            <tr className="border-gray-300 border-b">
              <td className="border-gray-300 border-r bg-gray-100 p-4 font-semibold text-gray-700">
                ຜົນການກວດສອບ
              </td>
              <td className="p-4 font-semibold text-sm">
                {getResultText(record?.overallResult)}
              </td>
            </tr>
            <tr>
              <td className="border-gray-300 border-r bg-gray-100 p-4 font-semibold text-gray-700">
                ສະຖານະ
              </td>
              <td className="p-4 font-semibold text-sm">
                {getStatusText(record?.status)}
              </td>
            </tr>
          </tbody>
        </table>

        {record?.summary && (
          <div className="mb-6 rounded-lg border-2 border-gray-300 bg-gray-50 p-4">
            <h4 className="mb-2 font-semibold text-gray-700">ສະຫຼຸບ:</h4>
            <p className="whitespace-pre-wrap text-sm leading-relaxed">
              {record.summary}
            </p>
          </div>
        )}
      </div>

      {/* Section 2: รายการตรวจแต่ละข้อ */}
      {record?.items && record.items.length > 0 && (
        <div className="mb-10">
          <h3 className="mb-6 border-gray-300 border-b-2 pb-2 font-bold text-gray-800 text-xl uppercase tracking-wide">
            2. ລາຍການກວດສອບ
          </h3>

          <div className="space-y-6">
            {record.items
              .sort((a, b) => a.orderIndex - b.orderIndex)
              .map((item) => {
                return (
                  <div
                    key={item.id}
                    className="mb-6 break-inside-avoid rounded-lg border-2 border-gray-300 bg-white p-6"
                  >
                    <div className="mb-4 flex items-start justify-between border-gray-200 border-b pb-3">
                      <h4 className="font-bold text-gray-800 text-lg">
                        {item.orderIndex + 1}. {item.title}
                      </h4>
                      <span className="font-semibold text-gray-700">
                        {getResultText(item.result)}
                      </span>
                    </div>

                    {item.description && (
                      <p className="mb-4 text-gray-700 text-sm leading-relaxed">
                        {item.description}
                      </p>
                    )}

                    {item.notes && (
                      <div className="mb-4 rounded bg-gray-50 p-3">
                        <p className="font-semibold text-gray-700 text-sm">
                          ຫມາຍເຫດ:
                        </p>
                        <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed">
                          {item.notes}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {/* Section 3: ไฟล์แนบ */}
      {record?.attachments && record.attachments.length > 0 && (
        <div className="mb-10">
          <h3 className="mb-6 border-gray-300 border-b-2 pb-2 font-bold text-gray-800 text-xl uppercase tracking-wide">
            3. ຟາຍແນບ
          </h3>

          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            {record.attachments.map((attachment) => {
              const imageUrl = resolveImageSrc(
                attachment.fileKey || attachment.fileUrl,
              );

              return (
                <div
                  key={attachment.id}
                  className="mb-4 break-inside-avoid rounded-lg border-2 border-gray-300 bg-white p-4"
                >
                  {imageUrl ? (
                    <img
                      src={imageUrl}
                      alt={attachment.description || "Attachment"}
                      className="max-w-full rounded border-2 border-gray-300"
                      style={{ maxHeight: "300px", objectFit: "contain" }}
                    />
                  ) : (
                    <div className="flex h-48 items-center justify-center rounded border-2 border-gray-300 bg-gray-50">
                      <span className="text-gray-500 text-sm">ບໍ່ພົບຮູບພາບ</span>
                    </div>
                  )}
                  {attachment.description && (
                    <p className="mt-2 text-center text-gray-600 text-xs">
                      {attachment.description}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="mt-12 border-gray-300 border-t-2 pt-6 text-center text-gray-600 text-sm">
        <p>ເອກະສານນີ້ຖືກສ້າງອັດຕະໂນມັດໂດຍລະບົບ Freedom ERP</p>
        <p className="mt-1">
          ວັນທີພິມ: {formatDateTimeLocal(new Date())} | ເລກທີບັນທຶກ: {record?.id}
        </p>
      </div>
    </div>
  );
}
